import { Component, inject, signal, computed } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../../shared/toast/toast.service';

type IntegrationTab = 'powerbi' | 'excel' | 'api';

@Component({
  selector: 'app-url-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ClipboardModule],
  templateUrl: './url-modal.component.html',
  styleUrls: ['./url-modal.component.css']
})
export class UrlModalComponent {

  powerBiScript: string = '';
  selectedFormat: 'csv' | 'parquet' = 'parquet';
  data: { id_report: number } = inject(DIALOG_DATA);
  baseUrl: string = '';

  activeTab = signal<IntegrationTab>('powerbi');
  displayedUrl = computed(() => {
    switch (this.activeTab()) {
      case 'excel': return `${this.baseUrl}?format=csv`;
      case 'api': return `${this.baseUrl}?format=json`;
      default: return this.baseUrl;
    }
  });
  tabLabel = computed(() => {
    switch (this.activeTab()) {
      case 'excel': return 'Excel';
      case 'api': return 'API';
      default: return 'Power BI';
    }
  });
  tabIcon = computed(() => {
    switch (this.activeTab()) {
      case 'excel': return '/assets/svg/excel-icon.svg';
      case 'api': return '/assets/svg/api-icon.svg';
      default: return '/assets/svg/powerbi-icon.svg';
    }
  });
  apiCurl = computed(() =>
    `curl -H "Authorization: Bearer <TU_TOKEN>" \\\n  "${this.baseUrl}?format=json"`
  );
  apiDataUrl = computed(() => `${this.baseUrl}/data`);
  apiDataCurl = computed(() =>
    `curl -H "Authorization: Bearer <TU_TOKEN>" \\\n  "${this.baseUrl}/data"`
  );
  copyDataUrl(): void {
    this.clipboard.copy(this.apiDataUrl());
  }

  generating = signal<boolean>(false);

  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private spinner = inject(NgxSpinnerService);
  private router = inject(Router);

  constructor(
    public dialogRef: DialogRef<string>,
    private clipboard: Clipboard
  ) {
    this.baseUrl = `${environment.API_URL_PUBLIC}/api/reports/last/${this.data.id_report}`;
    this.updatePowerBiScript();
  }

  selectTab(tab: IntegrationTab): void {
    this.activeTab.set(tab);
  }

  updatePowerBiScript(): void {
    if (this.selectedFormat === 'csv') {
      this.powerBiScript = `let\n    UrlParam = Text.From(Url),\n    TokenParam = Text.From(Token),\n    SourceBinary = Binary.Buffer(Web.Contents(UrlParam & \"?format=csv\", [Headers=[Authorization=\"Bearer \" & TokenParam]])),\n    CsvData = Csv.Document(SourceBinary, [Delimiter=\"|\", Encoding=1252, QuoteStyle=QuoteStyle.None])\nin\n    CsvData`;
    } else {
      this.powerBiScript = `let\n    UrlParam = Text.From(Url),\n    TokenParam = Text.From(Token),\n    SourceBinary = Binary.Buffer(Web.Contents(UrlParam & \"?format=parquet\", [Headers=[Authorization=\"Bearer \" & TokenParam]])),\n    ParquetData = Parquet.Document(SourceBinary)\nin\n    ParquetData`;
    }
  }

  copyUrl(): void {
    this.clipboard.copy(this.displayedUrl());
  }

  onClose(): void {
    this.dialogRef.close();
  }

  generateToken(): void {
    this.requestAndCopyToken(false);
  }

  generateBearer(): void {
    this.requestAndCopyToken(true);
  }

  private requestAndCopyToken(withBearerPrefix: boolean): void {
    if (this.generating()) return;

    if (!this.authService.hasValidSession()) {
      this.handleSessionExpired();
      return;
    }

    this.generating.set(true);
    this.spinner.show();

    this.authService.requestApiToken().pipe(
      finalize(() => {
        this.generating.set(false);
        this.spinner.hide();
      })
    ).subscribe({
      next: (res) => {
        const token = res?.object?.token;
        if (!token) {
          this.toastService.error('Error', 'La respuesta no contiene un token válido.');
          return;
        }
        const payload = withBearerPrefix ? `Bearer ${token}` : token;
        this.clipboard.copy(payload);
        this.toastService.success(
          withBearerPrefix ? 'Bearer copiado' : 'Token copiado',
          'Se copió al portapapeles. Vigencia: 30 días.'
        );
      },
      error: (err) => {
        if (err?.status === 401 || err?.status === 403) {
          return;
        }
        const msg =
          (err?.error && (typeof err.error === 'string' ? err.error : err.error?.message)) ||
          err?.message ||
          'No se pudo generar el token.';
        this.toastService.error('Error al generar token', msg);
      }
    });
  }

  private handleSessionExpired(): void {
    this.authService.logout();
    this.toastService.error('Sesión vencida', 'Tu sesión ha expirado. Vuelve a ingresar.');
    this.dialogRef.close();
    this.router.navigate(['/login']);
  }
}
